import React, { useEffect, useRef, useState } from 'react';
import stores from 'cd-eft/models/Stores';
import { observer } from 'mobx-react';
import { DynamicWaterwayProfile } from 'cd-eft/components/common/DynamicProfile/DynamicWaterwayProfile';
import { DynamicWaterwayHub } from 'cd-eft/models/Stores/DynamicProfile/Waterway/DynamicWaterwayHub';
import { ProfileStoreType } from 'cd-eft/models/Stores/DynamicProfile/DynamicProfileEnums';
import { EngineeringMapService } from 'cd-eft/framework/mapServices/EngineeringMapServices';
import { WaterwayDesignVM } from './WaterwayDesignVM';
import { DynamicTableStoreBase } from 'cd-eft/models/Stores/DynamicProfile/DynamicTableStoreBase';
import { DynamicProfileStoreBase } from 'cd-eft/models/Stores/DynamicProfile/DynamicProfileStoreBase';
import { Modal, ModalHeader, ModalBody, ModalFooter, Container } from 'reactstrap';
import { WaterWayDesignDetails } from './DDetails';
import { Accordion } from 'cd-eft/components/common/Accordion';
import { mdiCog, mdiPencil, mdiPlusBoxOutline, mdiTrashCanOutline, mdiPencilOffOutline, mdiDelete, mdiAsterisk, mdiHelpCircleOutline } from '@mdi/js';
import arrowCursorMove from 'cd-eft/assets/images/arrow-cursor-move.svg';
import Icon from '@mdi/react';
import { ActionsMenu } from 'cd-eft/components/Design/common/ActionsMenu';
import { DeleteElement } from 'cd-eft/components/Design/common/DeleteElement';
import Draggable from 'react-draggable';
import { PrintComponent } from 'cd-eft/components/common/PrintPDF';
import { Popup } from 'cd-eft/components/common/Popup';
import { WaterwayDesignReportComponent } from 'cd-eft/components/Reports/WaterwayDesignReport/WaterwayDesignReportComponent';
import { UserLeavingWarning } from 'cd-eft/components/common/UserLeavingWarning/UserLeavingWarning';
import { ProfileSettingsContainer } from '../../../../common/ProfileSettingsContainer/ProfileSettingsContainer';
import { StaticTooltip } from 'cd-eft/components/common/StaticTooltip/StaticTooltip';
import './WaterwayDesign.scss';
import { ButtonBlock } from 'cd-eft/components/Design/common/ButtonBlock/ButtonBlock';
import { ReachTable, ReachTableTabPane } from 'cd-eft/components/Design/common/ReachTable/ReachTable';
import { ResultsCard } from 'cd-eft/components/Design/common/ResultsCard/ResultsCard';
import { WaterwayLiningMaterialUpdate } from '../WaterwayLiningMaterialUpdate/WaterwayLiningMaterialUpdate';
import { AiDesignPanel } from 'cd-eft/components/Design/common/AiDesignPanel/AiDesignPanel';
import { action } from 'mobx';
import { D50_ROCK_RIPRAP } from '../WaterwayInput/WaterwayInputVM';
import { handleActionValueInputChange } from 'cd-eft/models/Stores/DesignStore';

const { designStore, waterwayDesignStore } = stores.AppStore;

// ai design experimental code
export const liningMaterialOptions = [
    { name: 'Concrete (0.013, 10)', value: 'Concrete (0.0130, 10)', manning: 0.0130, liningThickness: 10 },
    { name: D50_ROCK_RIPRAP, value: 'D50=6in Rock Riprap (0.0450, 9)', manning: 0.0450, liningThickness: 9 },
    { name: 'Gravel (0.029, 4)', value: 'Gravel (0.0290, 4)', manning: 0.0290, liningThickness: 4 },
    { name: 'Turf Reinforcement Mat (0.031, 0.63)', value: 'Turf Reinforcement Mat (0.0310, 0.63)', manning: 0.0310, liningThickness: 0.63 },
];
//End: ai design experimental code

const WaterwayDesignComponent = (props) => {
    let profileHub: DynamicWaterwayHub;

    const profileStoreRef = useRef<DynamicProfileStoreBase>();
    const profileTableStoreRef = useRef<DynamicTableStoreBase>();

    if (!profileTableStoreRef.current && !profileStoreRef.current) {
        profileHub = new DynamicWaterwayHub();
        profileHub.createStore(ProfileStoreType.MAP, { mapView: EngineeringMapService.instance?.mapView });
        profileHub.createStore(ProfileStoreType.PROFILE);
        profileStoreRef.current = profileHub.ProfileStore;
        profileHub.createStore(ProfileStoreType.TABLE);
        profileTableStoreRef.current = profileHub.TableStore;
    }

    const vmREF = useRef<WaterwayDesignVM>();
    if (!vmREF.current) {
        vmREF.current = new WaterwayDesignVM(
            {
                ...props,
                profileTableStore: profileTableStoreRef.current,
                profileStoreRef: profileStoreRef.current
            },
        );
    }

    const vm = vmREF.current;

    // ************** Profile Setup ************** //
    // Need to check if UI is fully rendered before drawing profile
    const [uiReady, setUiReady] = useState(false);
    const profileContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const checkDimensions = () => {
            if (profileContainerRef.current) {
                const { width, height } = profileContainerRef.current.getBoundingClientRect();

                if (width > 0 && height > 0) {
                    setUiReady(true);
                    return;
                }
            }

            requestAnimationFrame(checkDimensions);
        };

        checkDimensions();
    }, []);
    // ************ End Profile Setup ************ //

    useEffect(() => {
        vm.onMount();
        return vm.unMount;
    }, []);

    useEffect(() => {
        if (waterwayDesignStore.channelControl && waterwayDesignStore.currentWaterwayDesignElementTypeId) {
            vm.setReachTableFields();
        }
    }, [waterwayDesignStore.channelControl, waterwayDesignStore.currentWaterwayDesignElementTypeId]);

    useEffect(() => {
        vm.saveProfileSettingsFromDesignStoreToLocal();
    }, [waterwayDesignStore.profileSettings.bankProfile, waterwayDesignStore.profileSettings.fineGrid, waterwayDesignStore.profileSettings.lowBankOffset, waterwayDesignStore.profileSettings.sampleInterval, waterwayDesignStore.profileSettings.showCoord, waterwayDesignStore.profileSettings.verticalInterval])

    useEffect(() => {
        vm.resetValues();
    }, [designStore.toggleResetValues]);

    useEffect(() => {
        if (waterwayDesignStore.defaultLiningMaterialChanged[0]) {
            vm.checkAndApplyLiningMaterialChanges();
        }
    }, [waterwayDesignStore.defaultLiningMaterialChanged[0]])

    // const renderLiningMaterial = (station) => {
    //     if (waterwayDesignStore.waterwayType === 'Lined Waterway' || 
    //         waterwayDesignStore.currentWaterwayDesignElementTypeId === 19) {
    //         return station.capacity.replace("D50=6in Rock Riprap", "D₅₀=6in Rock Riprap") || station.capacity?.replace("D50=6in Rock Riprap", "D₅₀=6in Rock Riprap");
    //     } else {
    //         return station.capacity;
    //     }
    // };

    useEffect(() => {
        if (waterwayDesignStore.waterwaySummaryBannerReportObj) vm.setWaterwayResultsComment();
    }, [waterwayDesignStore.waterwaySummaryBannerReportObj])

    return (
        <div data-testid="waterwayDesign" className="waterway-design-container">
            <div ref={profileContainerRef} id="wdt-top-container">
                {designStore.displayResultBanner && vm.waterwaySummaryBannerReportObj !== undefined ?
                    <Accordion
                        className="accordion-wrapper results-info"
                        headers={['Results']}
                        expanded={true}
                        light={true}
                    >
                        <ResultsCard fields={vm.waterwayResultsFields} result={vm.waterwaySummaryBannerReportObj} comment={vm.waterwayResultsComment} >
                            <button type="button" className="usa-button px-3" onClick={vm.toggleReport}>
                                View PDF
                            </button>
                        </ResultsCard>
                    </Accordion> : null}

                <Accordion
                    className="accordion-wrapper profile-info"
                    headers={['Profile']}
                    expanded={true}
                    onClickInformationToggle={vm.onClickInformationToggle}
                    waterwayWizardStatus={vm.waterWayWizardStatus}
                    iconPathQ={true}
                    light={true}
                    actionButton={<ActionsMenu item="item" elementActions={vm.waterwayProfileActions}>Action</ActionsMenu>}
                >
                    <>
                        <DynamicWaterwayProfile profileStore={profileStoreRef.current} editable={false} generateInitialProfiles={vm.generateInitialProfiles} bankSelection={vm.savedProfileSettingsForm.showBankSelection} uiReady={uiReady} />
                        <div id="waterway-design-profile-btns">
                            <ButtonBlock
                                buttons={[
                                    {
                                        icon: mdiCog,
                                        title: 'Settings',
                                        disabled: waterwayDesignStore.checkAlignmentList || vm.inputSkipped,
                                        onClick: vm.openSettings,
                                        className: 'mr-auto',
                                    },
                                    {
                                        icon: mdiPencil,
                                        title: 'Digitize Reaches',
                                        disabled: waterwayDesignStore.checkAlignmentList || vm.inputSkipped,
                                        onClick: vm.sketchReach,
                                    },
                                    {
                                        icon: mdiPlusBoxOutline,
                                        title: 'Insert Station',
                                        alt: 'Insert Waterway Vertex',
                                        disabled: waterwayDesignStore.checkAlignmentList || vm.inputSkipped,
                                        onClick: vm.insertVertex,
                                    },
                                    {
                                        img: arrowCursorMove,
                                        title: 'Move Station',
                                        alt: 'Move Waterway Vertex',
                                        disabled: waterwayDesignStore.checkAlignmentList || vm.inputSkipped || (designStore.shouldHumanFeedbackOn && vm.hitUplimitHumanFeedback),
                                        onClick: vm.moveVertex,
                                        imgClassName: waterwayDesignStore.checkAlignmentList
                                            ? 'arrowCursorMove'
                                            : 'mr-2 arrowCursorMoveDisabled',
                                    },
                                    {
                                        icon: mdiTrashCanOutline,
                                        title: 'Delete Station',
                                        alt: 'Delete Waterway Vertex',
                                        disabled: waterwayDesignStore.checkAlignmentList || vm.inputSkipped || !vm.deleteStationButtonStatus,
                                        onClick: vm.deleteVertex,
                                    },
                                    {
                                        icon: mdiPencilOffOutline,
                                        title: '',
                                        alt: 'Stop Editing',
                                        disabled: waterwayDesignStore.checkAlignmentList || vm.inputSkipped,
                                        onClick: vm.stopEditing,
                                    },
                                ]}
                            />
                        </div>
                    </>
                </Accordion>
            </div>

            <div id="wdt-separator"></div>

            <div id="wdt-bottom-container">
                <Accordion
                    className="accordion-wrapper profile-reaches-info"
                    headers={['Waterway Profile Reaches']}
                    expanded={true}
                    onClickInformationToggle={vm.onClickInformationToggle}
                    waterwayWizardStatus={vm.waterWayWizardStatus}
                    light={true}
                    iconPathQ={true}
                >
                    <ReachTable tabs={vm.waterwayTabs} activeTab={vm.activeTab} setTab={vm.changeTab}>
                        {vm.waterwayTabs.map((el, index) => (
                            <ReachTableTabPane key={`ReachTableTabPane-${index}`} el={el} selectAll={vm.selectAll} activeTab={vm.activeTab} designType="waterway">

                                <tbody>
                                    {vm.currentObject.waterwayDesignStations && vm.currentObject.waterwayDesignStations.map((waterwayDesignStation: any, i: number) => (
                                        <tr
                                            id={'key-waterwayDesignStation-c-' + i}
                                            key={'key-waterwayDesignStation-c-' + i}
                                            className={`
                                                        ${waterwayDesignStore.selectedWaterwayStations.includes(waterwayDesignStation.reachId) ? 'selected-row' : ''}
                                                        ${waterwayDesignStore.isStationSelectedForWsc(waterwayDesignStation.reachId) ? 'highlighted-row' : ''}
                                                      `}
                                            onMouseEnter={(e) => vm.handleRowMouseEnter(e, i)}
                                            onMouseLeave={(e) => vm.handleRowMouseLeave(e, i)}
                                            hidden={vm.activeTab === 'reach-view' && waterwayDesignStation.reachId === "R-0"}
                                        >
                                            <td className="narrow-td text-center">
                                                {(waterwayDesignStation.reachId !== "START" && waterwayDesignStation.reachId !== "END" && waterwayDesignStation.reachId !== "R-0") && (
                                                    <input
                                                        id={'select' + i}
                                                        data-testid={'select' + i}
                                                        name="select"
                                                        type="checkbox"
                                                        aria-label={`Select station ${i}`}
                                                        checked={waterwayDesignStore.selectedWaterwayStations.includes(waterwayDesignStation.reachId)}
                                                        value={i}
                                                        onChange={(e) => {
                                                            vm.handleCheckboxChange(e, i);
                                                        }}
                                                    />
                                                )}
                                            </td>

                                            {/* // Status */}
                                            <td>
                                                <div className='status-container'>
                                                    <div
                                                        className={waterwayDesignStation.status && !['R-0', 'START', 'END'].includes(waterwayDesignStation.reachId) ? `status-label status--${waterwayDesignStation.status.replace(" ", "_")}` : ""}
                                                        data-testid={`status-${i}`}
                                                    >
                                                        {!['R-0', 'START', 'END'].includes(waterwayDesignStation.reachId) && waterwayDesignStation.status === 'WARNING_STATUS'
                                                            ? (<span> WARNING </span>)
                                                            : (<span> {['R-0', 'START', 'END'].includes(waterwayDesignStation.reachId)
                                                                ? ""
                                                                : waterwayDesignStation.status?.replace("_STATUS", "")}</span>)
                                                        }
                                                    </div>
                                                    {waterwayDesignStation.status &&
                                                        !['R-0', 'START', 'END'].includes(waterwayDesignStation.reachId) &&
                                                        ['WARNING_STATUS', 'ERROR_STATUS'].includes(waterwayDesignStation.status) && (
                                                            <button
                                                                className="question-button"
                                                                title=""
                                                                onClick={() => vm.onStatusInfoToggle(waterwayDesignStation.reachId)}
                                                            >
                                                                <StaticTooltip
                                                                    tooltipMessage={"More Information"}
                                                                    tooltipSide="bottom"
                                                                    id={`tooltip-${i}`}
                                                                    icon={mdiHelpCircleOutline}
                                                                    title=""
                                                                />
                                                            </button>
                                                        )}
                                                </div>
                                            </td>

                                            {el.defaultCols
                                                .filter(
                                                    el =>
                                                        (waterwayDesignStore.channelControl === 'PARABOLIC' && !el.trapezoidalOnly) ||
                                                        (waterwayDesignStore.channelControl === 'TRAPEZOIDAL' && !el.parabolicOnly)
                                                )
                                                // .filter(
                                                //     el =>
                                                //         !['soilStrength', 'vegCover', 'stability', 'capaVelocity'].includes(el.name) ||
                                                //         (['stabVelocity', 'capacity' ].includes(el.name) &&
                                                //             (waterwayDesignStore.waterwayType === 'Lined Waterway' ||
                                                //                 waterwayDesignStore.currentWaterwayDesignElementTypeId === 19)) ||
                                                //         !(waterwayDesignStore.waterwayType === 'Lined Waterway' ||
                                                //             waterwayDesignStore.currentWaterwayDesignElementTypeId === 19)
                                                // )
                                                .map((e, j) => (
                                                    // Empty Cells
                                                    ((waterwayDesignStation.reachId === 'R-0' && e.blankOnR0) ||
                                                        ((waterwayDesignStation.reachId === 'START' || waterwayDesignStation.reachId === 'END') && e.blankOnStartAndEnd)) ? (
                                                        <td key={'cptdkey-' + i + '-' + j} data-testid={`e.name-${i}`}></td>
                                                    ) : (
                                                        // Inputs
                                                        ((waterwayDesignStation.reachId !== 'START' &&
                                                            waterwayDesignStation.reachId !== 'END' &&
                                                            waterwayDesignStation.reachId !== 'R-0' &&
                                                            e.editable) ||
                                                            (waterwayDesignStation.reachId === 'R-0' && e.editableOnR0) ||
                                                            ((waterwayDesignStation.reachId === 'START' || waterwayDesignStation.reachId === 'END') && e.editableOnStartAndEnd)) ? (
                                                            <td key={'ctdkey-' + i + '-' + j}>
                                                                <div className="input-icon-container">
                                                                    <input
                                                                        className={`usa-input ${e.name !== 'addedQ' &&
                                                                            vm.formValidationReachTable.invalidFields.some(
                                                                                field => field.fieldName === e.name && field.index === i
                                                                            )
                                                                            ? 'usa-input--error'
                                                                            : ''
                                                                            }`}
                                                                        type="text"
                                                                        name={e.name}
                                                                        aria-label={`e.name-${i}`}
                                                                        data-testid={`e.name-${i}`}
                                                                        value={
                                                                            e.name === 'grade'
                                                                                ? (waterwayDesignStation.reachId === 'START' &&
                                                                                    waterwayDesignStore.startGradeNegative &&
                                                                                    waterwayDesignStation[e.name] !== '' &&
                                                                                    waterwayDesignStation[e.name] !== 0 &&
                                                                                    !String(waterwayDesignStation[e.name]).startsWith('-')) ||
                                                                                    (waterwayDesignStation.reachId === 'END' &&
                                                                                        waterwayDesignStore.endGradeNegative &&
                                                                                        waterwayDesignStation[e.name] !== '' &&
                                                                                        waterwayDesignStation[e.name] !== 0 &&
                                                                                        !String(waterwayDesignStation[e.name]).startsWith('-'))
                                                                                    ? `-${waterwayDesignStation[e.name]}`
                                                                                    : waterwayDesignStation[e.name]
                                                                                : waterwayDesignStation[e.name]
                                                                        }
                                                                        onChange={(event) => vm.handleInputChange(event, i)}
                                                                        onBlur={(event) => vm.handleTableInputChange(event, i)}
                                                                        onFocus={vm.handleTableFocus} />
                                                                    {((e.name === 'flowDepthFB' && waterwayDesignStation?.depthEdited) ||
                                                                        (e.name === 'flowTopWidth' && waterwayDesignStation?.widthEdited)) && (
                                                                            <Icon
                                                                                data-testid="asterisk-icon"
                                                                                path={mdiAsterisk}
                                                                                size={0.5}
                                                                                className="text-primary"
                                                                            />
                                                                        )}
                                                                </div>
                                                            </td>
                                                        ) : (
                                                            // Rounded Values
                                                            <td key={'cptdkey-' + i + '-' + j} data-testid={`e.name-${i}`}>
                                                                {e.name === 'capacity' && e.title === 'Lining Material'
                                                                    ? waterwayDesignStation[e.name].replace("D50=6in Rock Riprap", "D₅₀=6in Rock Riprap")
                                                                    : e.name === 'grade'
                                                                        ? waterwayDesignStation[e.name] === 0.0
                                                                            ? ''
                                                                            : -(waterwayDesignStation[e.name] * 100).toFixed(2)
                                                                        : e.name === 'stationLength'
                                                                            ? Number(waterwayDesignStation[e.name]).toFixed(1)
                                                                            : e.name === 'soilStrength' || e.name === 'vegCover'
                                                                                ? waterwayDesignStation[e.name].match(/^[0-9.]+/)?.[0]
                                                                                : e.name === 'reachId' && i === 1
                                                                                    ? ''
                                                                                    : waterwayDesignStation[e.name]}
                                                                {(e.name === 'soilStrength' || e.name === 'vegCover') &&
                                                                    waterwayDesignStation.reachId !== 'START' &&
                                                                    waterwayDesignStation.reachId !== 'END' ? (
                                                                    <StaticTooltip
                                                                        title="More Info"
                                                                        tooltipMessage={
                                                                            e.name === 'soilStrength'
                                                                                ? waterwayDesignStation.soilStrength.replace(' (', '--').split('--')[1]?.slice(0, -1)
                                                                                : waterwayDesignStation.vegCover.replace(' (', '--').split('--')[1]?.slice(0, -1)
                                                                        }
                                                                        tooltipSide={'right'}
                                                                        id={`reach-table-tt-${i}-${j}`}
                                                                    />
                                                                ) : null}
                                                            </td>
                                                        )
                                                    )
                                                ))}

                                            <td className={`${el.tab}-action-menu narrow-td action-td ${waterwayDesignStore.selectedWaterwayStations.includes(waterwayDesignStation.reachId) ? 'selected-action' : ''}`}>
                                                {(waterwayDesignStation.reachId !== "START" && waterwayDesignStation.reachId !== "END" && waterwayDesignStation.reachId !== "R-0") && (
                                                    <ActionsMenu i={i} item={waterwayDesignStation} elementActions={vm.setElementActions(el.tab, waterwayDesignStation.widthEdited, waterwayDesignStation.depthEdited)} />
                                                )}
                                            </td>
                                        </tr >))}
                                </tbody >
                            </ReachTableTabPane>
                        ))}
                    </ReachTable>
                </Accordion >


                <div id="waterway-design-btns">
                    <ButtonBlock buttons={[
                        {
                            title: 'Save Training Data',
                            disabled: vm.readyHumanFeedback,
                            onClick: vm.onReadyHumanFeedbackData,
                            className: 'mr-auto',
                            alt: "ai-training-data",
                        },
                        {
                            title: 'Close',
                            disabled: false,
                            onClick: vm.handleCancelEdits,
                            className: 'usa-button--outline',
                        },
                        {
                            title: 'Generate Surface',
                            disabled: vm.generateSurfaceButtonStatus,
                            onClick: vm.onClickGenerateSurface,
                        },
                        { title: 'Save', disabled: vm.saveButtonStatus, onClick: vm.onClickSave },
                        { title: 'Save & Calculate', disabled: vm.saveCalculateButtonStatus, onClick: vm.onClickSaveCalculate },
                    ]} />
                </div>
            </div>

            {/* Modals  */}
            {vm.isLiningMaterialPopupOpen[0] && (
                <WaterwayLiningMaterialUpdate
                    handleCloseLiningMaterialManualEntry={vm.handleCloseLiningMaterialManualEntry}
                    handleSaveLiningMaterialManualEntry={vm.handleSaveLiningMaterialManualEntry}
                    materialNameProp={vm.selectedReachLiningMaterial.liningMaterialName}
                    manningProp={vm.selectedReachLiningMaterial.manning}
                    liningThicknessProp={vm.selectedReachLiningMaterial.thickness}
                    key={vm.isLiningMaterialPopupOpen[1] as string}
                />
            )}

            {vm.onClickInformation ? (
                <Modal
                    isOpen={vm.onClickInformation}
                    toggle={vm.onClickInformationToggle}
                    centered={true}
                    className="eft-action-modal"
                    aria-label={vm.headerTitle}
                >
                    <ModalHeader toggle={vm.onClickInformationToggle} className='header-info-detail'>
                        <div className="display-flex flex-align-center">
                            <strong>{`Information Details on ${vm.headerTitle}`}</strong>
                        </div>
                        <a className="href-design-detail105" href="/design/designdetails" target="_blank">
                            <strong>Open Information in a New Window</strong>
                        </a>
                    </ModalHeader>
                    <ModalBody>
                        <Container>
                            <WaterWayDesignDetails title={vm.headerTitle} />
                        </Container>
                    </ModalBody>
                    <ModalFooter>
                        <button className="usa-button usa-button--outline" onClick={vm.onClickInformationToggle}>
                            Close
                        </button>
                    </ModalFooter>
                </Modal>
            ) : null}

            {vm.profileSettingsForm.profileSettingPopUpActive &&
                <Draggable
                    axis="both"
                    positionOffset={{ x: '-50%', y: '-50%' }}
                    cancel="input"
                >
                    <div className='profile-settings'>
                        <ProfileSettingsContainer
                            profileSettingsForm={vm.profileSettingsForm}
                            formValidation={vm.formValidation}
                            toggleFineGrid={vm.toggleFineGrid}
                            profileSettingsBankOnSelection={vm.profileSettingsBankOnSelection}
                            handleCancelProfileSettings={vm.handleCancelProfileSettings}
                            handleSaveProfileSettings={vm.handleSaveProfileSettings}
                            title={'Profile Settings'}
                            saveButtonText={'Apply'}
                            parentType='Waterway'
                        />
                    </div>
                </Draggable>
            }

            {/* ai design experimental code */}
            {vm.openAiDesignPanel &&
                <Draggable
                    axis="both"
                    positionOffset={{ x: '-50%', y: '-50%' }}
                    cancel="input, textarea, button, #not-draggable"
                >
                    <div className='ai-design-panel'>
                        <AiDesignPanel
                            elementId={vm.currentObject.alignedElementDesignParametersId}
                            handleCloseForm={action(() => { vm.openAiDesignPanel = false })}
                            onDesignEnds={vm.aiRefresh}
                            onReachesGenerated={vm.saveAiTrainingData}
                            profileStore={vm.profileStore}
                            title={'AI Assisted Designer'}
                        />
                    </div>
                </Draggable>
            }

            {vm.deleteStationDialogIsOpen && (
                <DeleteElement
                    trigger={vm.deleteStationDialogIsOpen}
                    setTrigger={vm.toggleDeleteStationDialog}
                    element="waterway station"
                    name={vm.waterwayDesignStationToDelete.reachId}
                    action={vm.deleteWaterwayDesignStation}
                />
            )}

            {vm.redigitizeReachesDialogIsOpen && (
                <Popup
                    trigger={vm.redigitizeReachesDialogIsOpen}
                    setTrigger={vm.toggleRedigitizeReachesDialog}
                    title={`Replace Channel Profile?`}
                    icon={mdiDelete}
                    handleCancel={vm.toggleRedigitizeReachesDialog}
                    cancelText="Close"
                    handleOK={vm.digitizeReaches}
                    okText="Ok"
                >
                    <p>{vm.notification.redigitizeReaches}</p>
                </Popup>
            )}
            {/* {vm.resetSettingDialogIsOpen && (
                <Popup
                    trigger={vm.resetSettingDialogIsOpen}
                    setTrigger={vm.toggleResetSettingDialog}
                    title={`Reset All?`}
                    icon={mdiDelete}
                    handleCancel={vm.toggleResetSettingDialog}
                    cancelText="Cancel"
                    handleOK={vm.resetSetting}
                    okText="Ok"
                >
                    <p>{vm.notification.resetSetting}</p>
                </Popup>
            )} */}
            {(vm.fixedValueDialogIsOpen && vm.fixedWidthOrDepth != 'none') && (
                <Popup
                    trigger={vm.fixedValueDialogIsOpen}
                    setTrigger={vm.toggleFixedValueDialog}
                    title={vm.fixedWidthOrDepth === 'width' ? 'Set Fixed Width' : 'Set Fixed Depth'}
                    handleCancel={vm.cancelFixedValueDialog}
                    cancelText="Close"
                    handleOK={vm.setFixedValue}
                    okText="Save"
                >
                    <p>Enter {vm.fixedWidthOrDepth} to be applied to every station.</p>
                    <span>{vm.fixedWidthOrDepth === 'width' ? 'Flow Top Width (ft)' : 'Flow Depth + FB (ft)'}</span>
                    <input
                        className="input-action-value"
                        name="fixedValue"
                        type="text"
                        value={vm.fixedValue}
                        onChange={(e) => handleActionValueInputChange(e, vm)}
                        onBlur={vm.handleActionValueInputBlur}
                    />
                </Popup>
            )}
            {vm.shiftElevationDialogIsOpen && (
                <Popup
                    trigger={vm.shiftElevationDialogIsOpen}
                    setTrigger={vm.toggleShiftElevationDialog}
                    title={'Shift Waterway Elevation'}
                    handleCancel={vm.cancelShiftElevationDialog}
                    cancelText="Close"
                    handleOK={vm.checkShiftElevation}
                    okText="Save"
                >
                    <p>Enter vertical shift to be applied to selected station.</p>
                    <span>{'Vertical Shift (ft)'}</span>
                    <input
                        className="input-action-value"
                        name="shiftElevation"
                        type="text"
                        value={vm.shiftElevation}
                        onChange={(e) => handleActionValueInputChange(e, vm)}
                        onBlur={vm.handleActionValueInputBlur}
                    />
                </Popup>
            )}
            {vm.isOpenGenerateReaches && (
                <Popup
                    trigger={vm.isOpenGenerateReaches}
                    setTrigger={vm.toggleGenerateReaches}
                    title={'Generate Reaches'}
                    handleCancel={action(() => vm.isOpenGenerateReaches = false)}
                    cancelText="Close"
                    handleOK={vm.runGenerateReaches}
                    okText="Generate"
                >
                    <div className='gr-form-grid'>
                        <div className='gr-form-group'>
                            <label htmlFor='gr-min-station'>{'Min Station (ft)'}</label>
                            <input
                                id="gr-min-station"
                                name="gr-min-station"
                                type="number"
                                value={vm.minStation}
                                onChange={action((e: React.ChangeEvent<HTMLInputElement>) => vm.minStation = Number(e.target.value))}
                            />
                        </div>
                        <div className='gr-form-group'>
                            <label htmlFor='gr-max-station'>{'Max Station (ft)'}</label>
                            <input
                                id="gr-max-station"
                                name="gr-max-station"
                                type="number"
                                value={vm.maxStation}
                                onChange={action((e: React.ChangeEvent<HTMLInputElement>) => vm.maxStation = Number(e.target.value))}
                            />
                        </div>
                        <div className='gr-form-group'>
                            <label htmlFor='gr-station-interval'>{'Station Interval (ft)'}</label>
                            <input
                                id="gr-station-interval"
                                name="gr-station-interval"
                                type="number"
                                value={vm.stationInterval}
                                onChange={action((e: React.ChangeEvent<HTMLInputElement>) => vm.stationInterval = Number(e.target.value))}
                            />
                        </div>
                        <div className='gr-form-group'>
                            <label htmlFor='gr-cut-depth'>{'Cut Depth (ft)'}</label>
                            <input
                                id="gr-cut-depth"
                                name="gr-cut-depth"
                                type="number"
                                value={vm.cupDepth}
                                onChange={action((e: React.ChangeEvent<HTMLInputElement>) => vm.cupDepth = Number(e.target.value))}
                            />
                        </div>
                        <div className="gr-form-group">
                            <label htmlFor="gr-lining-material">
                                Lining Material
                            </label>
                            <div className="">
                                <select
                                    data-testid="the-ComboBox"
                                    className="gr-form-select"
                                    name="gr-lining-material"
                                    value={vm.liningMaterial}
                                    onChange={action((e: React.ChangeEvent<HTMLSelectElement>) => vm.liningMaterial = e.target.value)}
                                    aria-label="Select Lining Material"
                                >
                                    {liningMaterialOptions.map((el, i) => (
                                        <option value={el.value} key={el.value}>
                                            {el.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <span className="usa-error-message" id={props.idAndName + '--err'} role="alert">
                                {props.errorMsg}
                            </span>
                        </div>
                    </div>
                </Popup>
            )}

            {vm.showStatusInfoModal ? (
                <div>
                    <Draggable axis="both" positionOffset={{ x: '10%', y: '150%' }} cancel="button,input">
                        <Modal
                            isOpen={vm.showStatusInfoModal}
                            toggle={vm.onStatusInfoToggle}
                            centered={false}
                            className="eft-action-modal"
                            aria-label="Information"
                            backdrop={false}
                        >
                            <ModalHeader className='header-info-detail'>
                                <div className="display-flex flex-align-center">
                                    <h1>{`Information`}</h1>
                                </div>
                            </ModalHeader>
                            <ModalBody>
                                <Container className='modal-description'>
                                    <p className='modal-description-item'><strong>Reach {vm.selectedStatusReachId}</strong></p>
                                    {vm.statusError.map((error, i) => (
                                        <div key={'status-entry-' + i}>
                                            <p className='modal-description-item'><strong>Error Code: {error.code}</strong></p>
                                            <p className='modal-description-description'>{error.message}</p>
                                        </div>
                                    ))}
                                </Container>
                            </ModalBody>
                            <ModalFooter>
                                <button className="usa-button" onClick={vm.onStatusInfoToggle}>
                                    Close
                                </button>
                            </ModalFooter>
                        </Modal>
                    </Draggable>
                </div>
            ) : null}

            {/* popups  */}
            {designStore.wdtGroupsLength > 0 && (
                <UserLeavingWarning
                    when={designStore.wdtGroupsLength > 0}
                    section="Waterway Design"
                    onOK={vm.userLeavingWarningOnOk}
                    okText="Return"
                    text={waterwayDesignStore.notifications.unsavedEdits}
                />
            )}
            <Modal className="project-owner-modal" isOpen={vm.saveButtonPopup}>
                <ModalBody>
                    <PrintComponent
                        componentToPrint={WaterwayDesignReportComponent}
                        printButtonText={'Report'}
                        functionProp={vm}
                        printButtonClasses={'usa-button usa-button--outline'}
                        //componentProps is used to pass the calculation results to the pdf
                        componentProps={{ ...vm.waterwaySummaryReportObj, project: designStore.projectsStore.currentProject }}
                        callFrom='WDTReport'
                    />
                </ModalBody>
            </Modal>
        </div >
    )
}

export const WaterwayDesign = observer(WaterwayDesignComponent);
